import React, { useEffect, useState } from 'react';
import { ShoppingBag, Menu, X, UserCircle2 } from 'lucide-react';
import { NavLink, Link } from 'react-router-dom';
import { NAV_LINKS } from '../mock';
import { useSapphires } from '../context/SapphireContext';
import { useAuth } from '../context/AuthContext';
import { useLang } from '../context/LanguageContext';
import LanguageSwitch from './LanguageSwitch';

// Map nav-link `name` (from mock.js) to translation keys
const NAV_KEY = {
  HOME: 'nav.home',
  ABOUT: 'nav.about',
  CRAFTING: 'nav.crafting',
  STORE: 'nav.store',
  NEWS: 'nav.news',
};

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { sapphires } = useSapphires();
  const { user } = useAuth();
  const { t } = useLang();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled ? 'bg-black/95 backdrop-blur-md' : 'bg-black/75'
      }`}
    >
      <div className="max-w-[1500px] mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        {/* Left Nav */}
        <nav className="hidden md:flex items-center gap-10 text-sm flex-1">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.name}
              to={l.to}
              end={l.to === '/'}
              className={({ isActive }) =>
                `font-nav nav-link ${isActive ? 'active gold-text' : ''}`
              }
            >
              {NAV_KEY[l.name] ? t(NAV_KEY[l.name]) : l.name}
            </NavLink>
          ))}
        </nav>

        {/* Center logo */}
        <Link to="/" className="absolute left-1/2 -translate-x-1/2 flex flex-col items-center justify-center">
          <div className="relative">
            <div className="w-12 h-12 rounded-full bg-gradient-to-b from-[#f5b400] to-[#8a5a00] flex items-center justify-center border-2 border-[#f5b400]/60 shadow-[0_0_20px_rgba(245,180,0,0.5)]">
              <span className="font-display font-black text-black text-lg leading-none">NR</span>
            </div>
            <span className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-3 bg-red-600 rounded-t-full" aria-hidden="true" />
          </div>
        </Link>

        {/* Right - sapphires bag + account */}
        <div className="hidden md:flex flex-1 justify-end items-center gap-5">
          <LanguageSwitch />
          <Link to="/store" className="font-nav text-sm text-white hover:text-[#f5b400] flex items-center gap-2 transition-colors">
            <ShoppingBag size={18} />
            <span>{t('nav.sapphires_bag')}</span>
            {sapphires > 0 && (
              <span className="ml-1 bg-[#f5b400] text-black rounded-full text-xs px-2 py-0.5 font-bold">
                {sapphires.toLocaleString()}
              </span>
            )}
          </Link>
          <NavLink
            to="/account"
            className={({ isActive }) =>
              `font-nav text-sm flex items-center gap-2 transition-colors ${
                isActive ? 'text-[#f5b400]' : 'text-white hover:text-[#f5b400]'
              }`
            }
            title={user ? user.username : t('nav.account')}
          >
            <UserCircle2 size={20} />
            <span className={user ? 'max-w-[120px] truncate' : 'whitespace-nowrap'}>{user ? user.username.toUpperCase() : t('nav.sign_in')}</span>
          </NavLink>
        </div>

        {/* Mobile toggle */}
        <button
          className="md:hidden text-white ml-auto"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label={t('nav.menu')}
        >
          {mobileOpen ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden bg-black/95 border-t border-white/10">
          <div className="px-6 py-6 flex flex-col gap-5">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.name}
                to={l.to}
                end={l.to === '/'}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  `font-nav ${isActive ? 'gold-text' : 'text-white'} hover:text-[#f5b400]`
                }
              >
                {NAV_KEY[l.name] ? t(NAV_KEY[l.name]) : l.name}
              </NavLink>
            ))}
            <Link to="/store" onClick={() => setMobileOpen(false)} className="font-nav text-white flex items-center gap-2">
              <ShoppingBag size={18} /> {t('nav.sapphires_bag')}
              {sapphires > 0 && (
                <span className="ml-1 bg-[#f5b400] text-black rounded-full text-xs px-2 py-0.5 font-bold">
                  {sapphires.toLocaleString()}
                </span>
              )}
            </Link>
            <NavLink
              to="/account"
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                `font-nav flex items-center gap-2 ${isActive ? 'gold-text' : 'text-white'} hover:text-[#f5b400]`
              }
            >
              <UserCircle2 size={20} /> {user ? user.username.toUpperCase() : t('nav.sign_in')}
            </NavLink>
            <div className="pt-2 border-t border-white/10">
              <LanguageSwitch />
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
